import {GameConfig} from '@gongxigames/framework/slot/types';
import {Sprite} from 'pixi.js';
import {Layer} from '@pixi/layers';
import {CoreDI, inject, ObjectBuilder, Tween, UpdateManager} from '@gongxigames/framework/core';
import {View} from '@gongxigames/framework/slot/fragments/module/view';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {IBackgroundView} from './IBackgroundView';
import {BackgroundConstants, BackgroundData} from './BackgroundConstants';

export class BackgroundView extends View implements IBackgroundView {
    public static readonly NAME: symbol = BackgroundConstants.backgroundViewName;
    public static layer: Layer = LAYERS.BACKGROUND;

    public static readonly LANDSCAPE: string = 'landscape';
    public static readonly PORTRAIT: string = 'portrait';
    public static readonly SQUARE_VIEW: string = 'square';
    public static readonly WIDE_VIEW: string = 'wide';

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _backgrounds: BackgroundData[] = [];
    protected _updater: UpdateManager = new UpdateManager(true);
    protected _mainView!: Sprite;
    protected _backgroundId = '';
    protected _backgroundAnimationId = '';
    protected isResourcesLoaded = false;

    public init(): void {
        super.init();

        this.updateManager.add(this._updater);
        this.initMainView();
        this.initScenes();
        this.playTransition(BackgroundConstants.BASE_VIEW);
    }

    protected initMainView(): void {
        this._mainView = this._objectBuilder.buildSprite(ObjectBuilder.resources.game_sprite.background);
        this.container.addChild(this._mainView);
    }

    protected initScenes(): void {
        this.setBackground(BackgroundConstants.BASE_VIEW);

        if (this._gameConfig.freeSpins && this._mainView.getChildByName(BackgroundConstants.FREE_SPINS_VIEW)) {
            this.setBackground(BackgroundConstants.FREE_SPINS_VIEW);
        }
    }

    public onResourcesLoaded(): void {
        this.isResourcesLoaded = true;
        this._objectBuilder.updateSprite(this._mainView, ObjectBuilder.resources.game_sprite.background);

        this._backgrounds.length = 0;
        this.initScenes();
        this.checkAnimationPlaying();

        this._backgrounds.forEach((data) => {
            if (data.id && data.view && data.id !== this._backgroundId) {
                data.view.alpha = 0;
            }
        });
    }

    public playTransition(backgroundId: string): void {
        if (this._backgroundId !== backgroundId && this._mainView.getChildByName(backgroundId)) {
            this._backgroundId = backgroundId;

            this._backgrounds.forEach((data) => {
                if (data.id && data.view) {
                    if (data.id === this._backgroundId) {
                        this.transitBackground(data.view, true, this.onTransitionFinished.bind(this));
                    } else {
                        this.transitBackground(data.view);
                    }
                }
            });
        } else {
            this.checkAnimationPlaying();
        }
    }

    protected transitBackground(value: Sprite, shouldShow = false, callBack?: Function): void {
        this.updateManager.add(new Tween(value)
            .to({alpha: shouldShow ? 1.0 : 0.0}, BackgroundConstants.TRANSFER_TIME)
            .onComplete(() => {
                callBack && callBack();
            })
            .start()
        );
    }

    protected onTransitionFinished(): void {
        this.checkAnimationPlaying();
    }

    protected checkAnimationPlaying(): void {
        if (this._backgroundAnimationId !== this._backgroundId) {
            this.clearAnimations();

            const currentView = this.getCurrentView();

            if (currentView && this.isResourcesLoaded) {
                if (this.isPlatformDesktop()) {
                    this.playAnimation(this.getBgContainer(currentView));
                } else {
                    this.playAnimation(this.getBgContainer(currentView.getChildByName<Sprite>(BackgroundView.LANDSCAPE)));
                    this.playAnimation(this.getBgContainer(currentView.getChildByName<Sprite>(BackgroundView.PORTRAIT)), true);
                }

                this._backgroundAnimationId = this._backgroundId;
            }
        }
    }

    protected getBgContainer(view: Sprite): Sprite {
        return view.getChildByName<Sprite>('bgContainer');
    }

    protected clearAnimations(): void {
        this._updater.removeAll();

        this._backgrounds.forEach((data) => {
            if (data.id && data.view) {
                const bgContainers: Sprite[] = [];

                if (this.isPlatformDesktop()) {
                    bgContainers.push(this.getBgContainer(data.view));
                } else {
                    bgContainers.push(this.getBgContainer(data.view.getChildByName<Sprite>(BackgroundView.LANDSCAPE)));
                    bgContainers.push(this.getBgContainer(data.view.getChildByName<Sprite>(BackgroundView.PORTRAIT)));
                }

                bgContainers.forEach((bgContainer) => {
                    if (bgContainer) {
                        const animationContainer = bgContainer.getChildByName<Sprite>('animationContainer');

                        if (animationContainer) {
                            animationContainer.removeChildren();
                        }
                    }
                });
            }
        });
    }

    public getCurrentView(): Sprite | null {
        return this._backgrounds
            .find((data) => data.id && data.view && data.id === this._backgroundId)
            ?.view ?? null;
    }

    protected playAnimation(container: Sprite, isPortrait = false): void {
        if (container) {
            const animationName = this.getAnimationName();
            const skinName = this.getSkinName();
            const background = container.getChildByName<Sprite>('bg');
            const animationContainer = container.getChildByName<Sprite>('animationContainer');

            if (animationName && animationContainer) {
                const fullAnimationName = this.getFullAnimationName(animationName, isPortrait);
                const animation = this.spineUtils.createAnimation(this.getSpineName(), fullAnimationName, true, false, 20, skinName);

                if (animation) {
                    animation.container.x = background.width / 2;
                    animation.container.y = background.height / 2;
                    animation.start();

                    animationContainer.addChild(animation.container);
                    this._updater.add(animation);
                }

                animationContainer.alpha = 0;
                this.transitBackground(animationContainer, true);
            }
        }
    }

    protected getSpineName(): string {
        return BackgroundConstants.SPINE_NAME;
    }

    protected getAnimationName(): string {
        return this._gameConfig.commonConfig?.backgroundAnimation!
            .find(data => data.backgroundId === this._backgroundId)?.animationName ?? '';
    }

    protected getSkinName(): string {
        return this._gameConfig.commonConfig?.backgroundAnimation!
            .find(data => data.backgroundId === this._backgroundId)?.skinName ?? '';
    }

    protected getFullAnimationName(animationName: string, isPortrait = false): string {
        if (this.isPlatformMobile()) {
            animationName += '_' + BackgroundView.WIDE_VIEW;
        }

        return isPortrait ? animationName + '_pm' : animationName;
    }

    public setBackground(backgroundId: string): void {
        const scene = this._mainView.getChildByName<Sprite>(backgroundId);
        const data: BackgroundData = {
            id: backgroundId,
            view: scene
        };

        this._backgrounds.push(data);
    }

    public onLandscape(): void {
        super.onLandscape();

        this._backgrounds.forEach((data) => {
            if (data.id && data.view) {
                data.view.getChildByName(BackgroundView.LANDSCAPE).visible = true;
                data.view.getChildByName(BackgroundView.PORTRAIT).visible = false;
            }
        });
    }

    public onPortrait(): void {
        super.onPortrait();

        this._backgrounds.forEach((data) => {
            if (data.id && data.view) {
                data.view.getChildByName(BackgroundView.LANDSCAPE).visible = false;
                data.view.getChildByName(BackgroundView.PORTRAIT).visible = true;
            }
        });
    }
}
